<template>
	<div
		class="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 py-4 px-3 sm:py-8 sm:px-6 overflow-x-hidden"
	>
		<div class="max-w-7xl mx-auto">
			<div class="mb-6 sm:mb-8">
				<button
					type="button"
					@click="previous"
					aria-label="Back to products"
					class="group inline-flex items-center text-gray-600 hover:text-gray-900 transition-all duration-200 bg-white px-4 py-2 rounded-lg shadow-sm hover:shadow-md"
				>
					<svg
						class="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					<span class="font-medium">Back to Products</span>
				</button>
			</div>

			<div
				v-if="selectedProduct"
				class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
			>
				<div class="lg:col-span-1 space-y-6">
					<div
						class="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
					>
						<div
							class="bg-gradient-to-br from-blue-600 to-blue-700 p-4 sm:p-6 text-white"
						>
							<div class="flex items-start justify-between mb-4">
								<img
									:src="productLogo"
									alt="Product Logo"
									loading="lazy"
									decoding="async"
									class="w-12 h-12 sm:w-16 sm:h-16 max-w-full object-contain rounded-xl bg-white p-2 shadow-lg"
								/>
								<span
									class="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold"
								>
									{{ coverType }}
								</span>
							</div>
							<h1
								class="text-lg sm:text-2xl font-bold mb-2 leading-tight break-words"
							>
								{{ productName }}
							</h1>
							<p class="text-blue-100 text-sm">
								{{ insuranceCategory }} Insurance
							</p>
						</div>

						<div
							class="p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white"
						>
							<div class="text-center">
								<p
									class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2"
								>
									Base Premium
								</p>
								<p
									class="text-2xl sm:text-4xl font-bold text-black mb-1"
								>
									{{ formattedRate.toLocaleString() }}
								</p>
								<p class="text-sm text-gray-600">
									{{ premiumRange }}
								</p>
							</div>
						</div>
						<!---Rating Card-->
						<div
							class="flex items-center gap-1 justify-center mx-auto"
						>
							<!-- Star Rating Display (5 stars with precise partial fill) -->
							<div class="flex space-x-0.5">
								<div
									v-for="n in 5"
									:key="n"
									class="relative w-5 h-5"
								>
									<!-- Empty/Gray star (background) -->
									<svg
										class="w-5 h-5 text-gray-300"
										fill="currentColor"
										viewBox="0 0 20 20"
										aria-hidden="true"
									>
										<path
											d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
										/>
									</svg>

									<!-- Filled/Yellow star (clipped to show partial fill) -->
									<div
										class="absolute inset-0 overflow-hidden"
										:style="{
											width:
												Math.max(
													0,
													Math.min(
														100,
														(selectedProduct.averageRate -
															(n - 1)) *
															100,
													),
												) + '%',
										}"
									>
										<svg
											class="w-5 h-5 text-yellow-400"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
											/>
										</svg>
									</div>
								</div>
							</div>

							<!-- Rating summary text -->
							<div class="flex items-center text-sm">
								<!-- <span class="font-medium text-gray-900">
      {{ selectedProduct.averageRate.toFixed(1) }}
    </span> -->
								<span class="mx-1 text-gray-500">•</span>
								<span class="text-gray-600">
									({{ reviews.length }}
									{{
										reviews.length === 1
											? 'rating'
											: 'ratings'
									}})
								</span>
							</div>
						</div>
						<div
							class="p-4 sm:p-6 space-y-3 border-t border-gray-100"
						>
							<button
								type="button"
								@click="selectProduct"
								aria-label="Get quote and buy"
								class="w-full py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center group"
							>
								<svg
									class="w-5 h-5 mr-2 transition-transform group-hover:scale-110"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
									/>
								</svg>
								Get Quote & Buy
							</button>
							<button
								type="button"
								@click="openHelp"
								aria-label="Open help and intermediaries"
								class="w-full py-3 bg-green-50 text-green-700 font-medium rounded-xl hover:bg-green-100 transition-all duration-200 border border-green-200 flex items-center justify-center"
							>
								<svg
									class="w-5 h-5 mr-2"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								Need Help?
							</button>
						</div>
					</div>

					<div
						class="bg-white rounded-2xl shadow-lg p-6 border border-gray-100"
					>
						<h3
							class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4"
						>
							Underwritten By
						</h3>
						<div class="flex items-center justify-between mb-4">
							<div class="flex-1 min-w-0 break-words">
								<p class="font-semibold text-gray-900">
									{{ underwriterName }}
								</p>
								<p
									class="text-xs text-gray-500 mt-1"
									v-if="underwriterContact"
								>
									{{ underwriterContact }}
								</p>
							</div>
						</div>
						<button
							type="button"
							v-if="resolveUnderwriterId()"
							@click="
								viewUnderwriterById(selectedProduct.underwriter)
							"
							aria-label="View underwriter profile"
							class="w-full py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-200"
						>
							View Full Profile
						</button>
					</div>

					<div
						class="bg-white rounded-2xl shadow-lg p-6 border border-gray-100"
					>
						<h3
							class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4"
						>
							Coverage Details
						</h3>
						<div class="space-y-3">
							<div
								class="flex justify-between items-center pb-3 border-b border-gray-100"
							>
								<span class="text-sm text-gray-600"
									>Duration</span
								>
								<span
									class="text-sm font-semibold text-gray-900"
									>{{ coveragePeriod }}</span
								>
							</div>
							<div
								class="flex justify-between items-center pb-3 border-b border-gray-100"
							>
								<span class="text-sm text-gray-600"
									>Currency</span
								>
								<span
									class="text-sm font-semibold text-gray-900"
									>{{ supportedCurrency }}</span
								>
							</div>
							<div
								class="flex justify-between items-center pb-3 border-b border-gray-100"
							>
								<span class="text-sm text-gray-600">Class</span>
								<span
									class="text-sm font-semibold text-gray-900"
									>{{ insuranceClass }}</span
								>
							</div>
							<div class="flex justify-between items-center">
								<span class="text-sm text-gray-600"
									>Status</span
								>
								<span
									class="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full"
									>Active</span
								>
							</div>
						</div>
					</div>
				</div>

				<div class="lg:col-span-2 space-y-6">
					<!-- Description Card -->
					<div
						class="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100"
					>
						<div class="flex items-center mb-4">
							<div
								class="w-1 h-6 bg-blue-600 rounded-full mr-3"
							></div>
							<h2 class="text-xl font-bold text-gray-900">
								About This Product
							</h2>
						</div>
						<p class="text-gray-700 leading-relaxed">
							{{ productDescription }}
						</p>
					</div>

					<!-- Requirements & Terms: consolidated responsive accordions -->
					<div class="space-y-6">
						<!-- Requirements (single block; open on desktop, collapsed on mobile) -->
						<details
							:open="isRequirementsOpen"
							class="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100"
							@toggle="isRequirementsOpen = $event.target.open"
						>
							<summary
								class="flex items-center justify-between font-semibold cursor-pointer"
							>
								<span class="flex items-center"
									><span
										class="w-1 h-6 bg-orange-500 rounded-full mr-3"
									></span
									>Required Documents</span
								>
								<span class="text-sm text-gray-500">{{
									requirements.length
								}}</span>
							</summary>
							<div class="mt-3 flex flex-wrap gap-2">
								<span
									v-for="req in requirements"
									:key="req.name"
									class="px-4 py-2 bg-orange-50 text-orange-700 text-sm font-medium rounded-lg border border-orange-200"
								>
									{{ req.name }}
								</span>
							</div>
						</details>

						<!-- Terms (single block; open on desktop, collapsed on mobile) -->
						<details
							:open="isTermsOpen"
							class="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100"
							@toggle="isTermsOpen = $event.target.open"
						>
							<summary
								class="flex items-center justify-between font-semibold cursor-pointer"
							>
								<span class="flex items-center"
									><span
										class="w-1 h-6 bg-gray-600 rounded-full mr-3"
									></span
									>Terms & Conditions</span
								>
								<span class="text-sm text-gray-500"
									>View Document</span
								>
							</summary>
							<div class="mt-3">
								<p class="text-gray-600 mb-3">
									Review the complete terms and conditions
									before purchasing this product.
								</p>
								<a
									:href="productTnc"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="View product terms and conditions"
									class="inline-flex items-center px-4 py-2 bg-gray-50 text-gray-700 font-medium rounded-lg hover:bg-gray-100 border border-gray-200 transition-all duration-200"
								>
									<svg
										class="w-5 h-5 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										></path>
									</svg>
									View Full Document
								</a>
							</div>
						</details>
					</div>

					<!-- Benefits Tabs -->
					<div
						class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
					>
						<div class="border-b border-gray-200">
							<div
								class="flex flex-col sm:flex-row"
								role="tablist"
								aria-label="Benefits Tabs"
							>
								<button
									type="button"
									id="tab-embedded-button"
									@click="activeTab = 'embedded'"
									:aria-selected="activeTab === 'embedded'"
									aria-controls="tab-embedded-panel"
									:tabindex="
										activeTab === 'embedded' ? 0 : -1
									"
									@keydown.left.prevent="switchTab('prev')"
									@keydown.right.prevent="switchTab('next')"
									:class="[
										'flex-1 py-3 sm:py-4 px-3 sm:px-6 font-semibold text-sm sm:text-base transition-all duration-200',
										activeTab === 'embedded'
											? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
											: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
									]"
								>
									Core Benefits ({{
										embeddedBenefits.length
									}})
								</button>
								<button
									type="button"
									id="tab-optional-button"
									@click="activeTab = 'optional'"
									:aria-selected="activeTab === 'optional'"
									aria-controls="tab-optional-panel"
									:tabindex="
										activeTab === 'optional' ? 0 : -1
									"
									@keydown.left.prevent="switchTab('prev')"
									@keydown.right.prevent="switchTab('next')"
									:class="[
										'flex-1 py-3 sm:py-4 px-3 sm:px-6 font-semibold text-sm sm:text-base transition-all duration-200',
										activeTab === 'optional'
											? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
											: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
									]"
								>
									Optional Benefits ({{
										optionalBenefits.length
									}})
								</button>
							</div>
						</div>

						<div class="p-4 sm:p-6" ref="benefits">
							<div
								v-show="activeTab === 'embedded'"
								id="tab-embedded-panel"
								role="tabpanel"
								aria-labelledby="tab-embedded-button"
							>
								<div
									v-if="embeddedBenefits.length"
									class="grid grid-cols-1 sm:grid-cols-2 gap-4"
								>
									<div
										v-for="benefit in embeddedBenefits"
										:key="benefit.benefitSystemRef"
										class="group p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all duration-200"
									>
										<div class="flex items-start">
											<div
												class="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mr-3 group-hover:scale-110 transition-transform"
											>
												<svg
													class="w-5 h-5 text-white"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fill-rule="evenodd"
														d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
													/>
												</svg>
											</div>
											<div class="flex-1 min-w-0">
												<h4
													class="font-semibold text-gray-900 mb-1"
												>
													{{
														benefit.benefit_category_name
													}}
												</h4>
												<p
													class="text-xs text-gray-600 mb-2 break-words"
												>
													{{
														benefit.benefit_category_description
													}}
												</p>
												<div
													class="flex items-center text-xs"
												>
													<span
														class="text-gray-500 mr-2"
														>Limit:</span
													>
													<span
														class="font-semibold text-blue-700"
														>{{
															formatBenefitLimit(
																benefit.benefitLimit,
															)
														}}</span
													>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div v-else class="text-center py-12">
									<svg
										class="w-16 h-16 mx-auto text-gray-300 mb-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
										/>
									</svg>
									<p class="text-gray-500">
										No core benefits available
									</p>
								</div>
							</div>

							<div v-show="activeTab === 'optional'">
								<div
									v-if="optionalBenefits.length"
									class="grid grid-cols-1 sm:grid-cols-2 gap-4"
								>
									<div
										v-for="benefit in optionalBenefits"
										:key="benefit.benefitSystemRef"
										class="group p-4 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all duration-200"
									>
										<div class="flex items-start">
											<div
												class="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 mr-3 group-hover:scale-110 transition-transform"
											>
												<svg
													class="w-5 h-5 text-white"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
													/>
												</svg>
											</div>
											<div class="flex-1 min-w-0">
												<h4
													class="font-semibold text-gray-900 mb-1"
												>
													{{
														benefit.benefit_category_name
													}}
												</h4>
												<p
													class="text-xs text-gray-600 mb-2 break-words"
												>
													{{
														benefit.benefit_category_description
													}}
												</p>
												<div
													class="flex items-center text-xs"
												>
													<span
														class="text-gray-500 mr-2"
														>Limit:</span
													>
													<span
														class="font-semibold text-purple-700"
														>{{
															formatBenefitLimit(
																benefit.benefitLimit,
															)
														}}</span
													>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div v-else class="text-center py-12">
									<svg
										class="w-16 h-16 mx-auto text-gray-300 mb-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 6v6m0 0v6m0-6h6m-6 0H6"
										/>
									</svg>
									<p class="text-gray-500">
										No optional benefits available
									</p>
								</div>
							</div>
						</div>
					</div>

					<div
						class="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100"
					>
						<div class="flex items-center mb-4">
							<div
								class="w-1 h-6 bg-gray-600 rounded-full mr-3"
							></div>
							<h2 class="text-xl font-bold text-gray-900">
								Customer Reviews
							</h2>
						</div>

						<div v-if="paginatedReviews.length" class="space-y-4">
							<div
								v-for="review in paginatedReviews"
								:key="review.id"
								class="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200"
							>
								<div class="flex items-start space-x-3">
									<img
										:src="avatarLink"
										alt="Reviewer"
										loading="lazy"
										decoding="async"
										class="w-10 h-10 sm:w-12 sm:h-12 max-w-full rounded-full border-2 border-white shadow-sm"
									/>
									<div class="flex-1 min-w-0">
										<div
											class="flex items-center justify-between mb-2"
										>
											<h4
												class="font-semibold text-gray-900"
											>
												{{
													review.client.names
														.firstName
												}}
											</h4>
											<div class="flex space-x-0.5">
												<svg
													v-for="star in 5"
													:key="star"
													class="w-4 h-4"
													:class="
														star <= review.rate
															? 'text-yellow-400'
															: 'text-gray-300'
													"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
													/>
												</svg>
											</div>
										</div>
										<p
											class="text-sm text-gray-600 break-words"
										>
											{{ review.review }}
										</p>
									</div>
								</div>
							</div>
						</div>
						<div v-else class="text-center py-12">
							<svg
								class="w-16 h-16 mx-auto text-gray-300 mb-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
								/>
							</svg>
							<p class="text-gray-500 text-sm">
								No reviews yet. Be the first to review by
								purchasing this product!
							</p>
						</div>

						<div
							v-if="reviews.length > reviewsPerPage"
							class="flex items-center justify-between mt-6 pt-6 border-t border-gray-200"
						>
							<button
								@click="
									currentPage = Math.max(currentPage - 1, 1)
								"
								:disabled="currentPage === 1"
								class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
							>
								Previous
							</button>
							<span class="text-sm text-gray-600"
								>Page {{ currentPage }} of
								{{ totalPages }}</span
							>
							<button
								@click="
									currentPage = Math.min(
										currentPage + 1,
										totalPages,
									)
								"
								:disabled="currentPage === totalPages"
								class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
							>
								Next
							</button>
						</div>
					</div>
				</div>
			</div>

			<div
				v-else
				class="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center"
			>
				<svg
					class="w-20 h-20 mx-auto text-gray-300 mb-6"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
					/>
				</svg>
				<h3 class="text-2xl font-bold text-gray-700 mb-3">
					No Product Selected
				</h3>
				<p class="text-gray-600 mb-6">
					Please select a product to view its details
				</p>
				<button
					type="button"
					@click="$router.push({ name: 'ProductQuotation' })"
					aria-label="Browse products"
					class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
				>
					Browse Products
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { FetchIntermediaries } from '../../../api/insurance';
import { fetchProductReviewsApi } from '../../../../newportal/api/portal';

export default {
	name: 'ViewProductDetails',
	props: {
		product: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			activeTab: 'embedded',
			reviews: [],
			avatarLink:
				'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTIJxOLfDct_vEPdS-6OsRnS-kDl_HCv5nI2A&s',
			currentPage: 1,
			reviewsPerPage: 3,
			// new responsive controls
			isMobile: false,
			isRequirementsOpen: true,
			isTermsOpen: true,
		};
	},
	mounted() {
		this.updateIsMobile();
		this._resizeHandler = this.updateIsMobile.bind(this);
		window.addEventListener('resize', this._resizeHandler);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this._resizeHandler);
	},
	computed: {
		...mapGetters('product', ['selectedProduct']),
		...mapGetters('quotation', ['selectQuotationData']),

		// Product display data
		productLogo() {
			return this.selectedProduct && this.selectedProduct.productLogo
				? this.selectedProduct.productLogo
				: 'https://api.prod.nexus.ke/static/default-logo.webp';
		},
		productName() {
			return this.selectedProduct && this.selectedProduct.productName
				? this.selectedProduct.productName
				: 'Product Name Not Available';
		},
		productDescription() {
			return this.selectedProduct &&
				this.selectedProduct.productDescription
				? this.selectedProduct.productDescription
				: 'No description available for this product.';
		},
		insuranceCategory() {
			return this.selectedProduct &&
				this.selectedProduct.insuranceCategory
				? this.selectedProduct.insuranceCategory
				: 'N/A';
		},
		insuranceClass() {
			return this.selectedProduct && this.selectedProduct.insuranceClass
				? this.selectedProduct.insuranceClass
				: 'N/A';
		},
		coverType() {
			let type =
				this.selectedProduct && this.selectedProduct.covertypeSystemRef
					? this.selectedProduct.covertypeSystemRef
					: 'N/A';
			let typeMap = {
				COMP: 'Comprehensive',
				TPO: 'Third Party Only',
				TPFT: 'Third Party Fire & Theft',
			};
			return typeMap[type] || type;
		},
		supportedCurrency() {
			var sc =
				this.selectedProduct && this.selectedProduct.supportedCurrency
					? this.selectedProduct.supportedCurrency
					: [];
			return sc.join(', ') || 'KES';
		},

		// Premium and pricing
		formattedRate() {
			let rate =
				this.selectedProduct && this.selectedProduct.basePremiumRate
					? this.selectedProduct.basePremiumRate
					: null;
			if (!rate || rate.value == null) return 'Not Provided';
			if (rate.rateType === 'fixed') {
				return 'KES ' + rate.value.toLocaleString();
			}
			if (rate.rateType === 'percentage' && this.selectQuotationData) {
				return parseInt(
					(
						rate.value * this.selectQuotationData.vehicleValue
					).toFixed(2) /
						100 +
						'',
				);
			}
			return 'Invalid Rate Type';
		},
		premiumRange() {
			let premium =
				this.selectedProduct && this.selectedProduct.productPremium
					? this.selectedProduct.productPremium
					: null;
			if (!premium) return '';
			var min = premium.minimum ? premium.minimum.toLocaleString() : '0';
			var max = premium.maximum ? premium.maximum.toLocaleString() : '0';
			return 'Range: KES ' + min + ' - ' + max;
		},

		// Coverage details
		coveragePeriod() {
			var periods =
				this.selectedProduct &&
				this.selectedProduct.productCover &&
				this.selectedProduct.productCover.supportedPeriods
					? this.selectedProduct.productCover.supportedPeriods
					: [];
			if (periods.length === 0) return 'Not specified';
			var days = periods
				.map(function (p) {
					return p + ' days';
				})
				.join(', ');
			return days;
		},

		// Underwriter info
		underwriterName() {
			if (
				this.selectedProduct &&
				this.selectedProduct.underwriter &&
				this.selectedProduct.underwriter.name
			)
				return this.selectedProduct.underwriter.name;
			if (
				this.selectedProduct &&
				this.selectedProduct.underwriterSystemRef
			)
				return this.selectedProduct.underwriterSystemRef;
			return 'Not specified';
		},
		underwriterContact() {
			var contacts =
				this.selectedProduct &&
				this.selectedProduct.underwriter &&
				this.selectedProduct.underwriter.contacts
					? this.selectedProduct.underwriter.contacts
					: [];
			return contacts.length > 0 ? contacts[0] : null;
		},

		// Benefits
		embeddedBenefits() {
			return this.selectedProduct && this.selectedProduct.productBenefits
				? this.selectedProduct.productBenefits.filter(function (b) {
						return b.benefitType === 'embedded';
					})
				: [];
		},
		optionalBenefits() {
			return this.selectedProduct && this.selectedProduct.productBenefits
				? this.selectedProduct.productBenefits.filter(function (b) {
						return b.benefitType === 'optional';
					})
				: [];
		},

		// Requirements
		requirements() {
			var req =
				this.selectedProduct && this.selectedProduct.productRequirements
					? this.selectedProduct.productRequirements
					: null;
			return Array.isArray(req) ? req : [];
		},

		// Terms & Conditions
		productTnc() {
			return this.selectedProduct && this.selectedProduct.productTnc
				? this.selectedProduct.productTnc.uploadPath
				: 'https://api.prod.nexus.ke/static/default-terms.pdf';
		},

		// Reviews
		paginatedReviews() {
			var start = (this.currentPage - 1) * this.reviewsPerPage;
			var end = start + this.reviewsPerPage;
			return this.reviews.slice(start, end);
		},
		totalPages() {
			return Math.ceil(this.reviews.length / this.reviewsPerPage);
		},
		averageRating() {
			if (this.reviews.length === 0) return 0;
			var sum = this.reviews.reduce(function (acc, review) {
				return acc + review.rating;
			}, 0);
			return sum / this.reviews.length;
		},
	},

	methods: {
		...mapActions('help', ['setEntities', 'setEntityData']),

		resolveUnderwriterId() {
			var p = this.selectedProduct || this.product || {};
			return (
				p.underwriterSystemRef ||
				(p.underwriter &&
					p.underwriter[0] &&
					(p.underwriter[0].id ||
						p.underwriter[0].underwriterSystemRef ||
						p.underwriter[0].underwriterId)) ||
				null
			);
		},

		viewUnderwriterById(underwriter) {
			var id = this.resolveUnderwriterId();
			if (!id) return;
			this.$router.push({ name: 'ViewUnderwriter', params: { id: id } });
			this.setEntityData(underwriter);
		},

		formatBenefitLimit: function formatBenefitLimit(benefitLimit) {
			const minimum = benefitLimit && benefitLimit.minimum;
			const maximum = benefitLimit && benefitLimit.maximum;
			const defaultLimit = benefitLimit && benefitLimit.defaultLimit;
			if (defaultLimit > 0) {
				return 'KES ' + defaultLimit.toLocaleString();
			}
			if (minimum > 0 || maximum > 0) {
				const min = minimum ? minimum.toLocaleString() : 0;
				const max = maximum ? maximum.toLocaleString() : 0;
				return 'KES ' + min + ' - ' + max;
			}
			return 'Adjustable';
		},

		async openHelp() {
			try {
				const res = await FetchIntermediaries();
				if (res && res.status) {
					this.setEntities(res.data);
					this.$router.push({ name: 'HelpMotor' });
				}
			} catch (error) {
				console.error('Error fetching intermediaries:', error);
				this.$router.push({ name: 'Home' });
			}
		},

		async fetchProductReviews() {
			const payload = {
				productSystemRef: this.selectedProduct.productSystemRef,
			};
			try {
				const res = await fetchProductReviewsApi(payload);
				this.reviews = res.data;
			} catch (error) {}
		},

		previous() {
			this.$router.push({ name: 'ProductQuotation' });
		},

		selectProduct() {
			this.$router.push({ name: 'BasicCoverDetails' });
		},
		switchTab(direction) {
			const tabs = ['embedded', 'optional'];
			let idx = tabs.indexOf(this.activeTab);
			if (direction === 'next') idx = (idx + 1) % tabs.length;
			if (direction === 'prev')
				idx = (idx - 1 + tabs.length) % tabs.length;
			this.activeTab = tabs[idx];
			// focus the active tab button for keyboard users
			this.$nextTick(() => {
				const el = document.getElementById(
					this.activeTab === 'embedded'
						? 'tab-embedded-button'
						: 'tab-optional-button',
				);
				if (el) el.focus();
			});
		},
		updateIsMobile() {
			const mobile = window.matchMedia('(max-width: 640px)').matches;
			if (mobile !== this.isMobile) {
				this.isMobile = mobile;
				// collapse on mobile, expand on desktop
				this.isRequirementsOpen = !mobile;
				this.isTermsOpen = !mobile;
			}
		},
	},

	watch: {
		activeTab: function activeTab() {
			// Scroll benefits section into view when switching tabs (better UX than top-of-page)
			this.$nextTick(() => {
				if (this.$refs.benefits && this.$refs.benefits.scrollIntoView) {
					this.$refs.benefits.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					});
				}
			});
		},
	},
	async mounted() {
		await this.fetchProductReviews();
		console.log('==product', this.selectQuotationData);
	},
};
</script>

<style scoped>
/* Smooth animations */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.animate-fade-in-up {
	animation: fadeInUp 0.5s ease-out forwards;
}

::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

::-webkit-scrollbar-track {
	background: #f1f5f9;
	border-radius: 10px;
}

::-webkit-scrollbar-thumb {
	background: linear-gradient(180deg, #3b82f6, #2563eb);
	border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
	background: linear-gradient(180deg, #2563eb, #1d4ed8);
}

* {
	transition-property:
		background-color, border-color, color, fill, stroke, opacity,
		box-shadow, transform;
	transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	transition-duration: 150ms;
}

/* Focus styles for accessibility */
button:focus-visible,
a:focus-visible {
	outline: 2px solid #3b82f6;
	outline-offset: 2px;
}

/* Responsive grid improvements */
@media (max-width: 1024px) {
	.lg\:col-span-1,
	.lg\:col-span-2 {
		grid-column: span 1;
	}
}

/* Enhanced mobile touch targets */
@media (max-width: 768px) {
	button,
	a {
		min-height: 44px;
	}
}

/* Print styles */
@media print {
	.bg-gradient-to-br,
	.bg-gradient-to-r {
		background: white !important;
		color: black !important;
	}

	button {
		display: none;
	}
}

/* Reduced motion for accessibility */
@media (prefers-reduced-motion: reduce) {
	*,
	*::before,
	*::after {
		animation-duration: 0.01ms !important;
		animation-iteration-count: 1 !important;
		transition-duration: 0.01ms !important;
	}
}

/* Mobile-friendly <details> summary styling */
details[open] > summary::after {
	content: '';
}
summary {
	list-style: none;
}
summary::-webkit-details-marker {
	display: none;
}
summary {
	cursor: pointer;
}
</style>
